import { SCOPES } from './scope.config.js'
import { ROUTE_PATHS } from './route.config.js'

/** localStorage key for the "user has signed in before" hint. */
export const AUTH_HINT_KEY = 'vn_auth'

/** Mirrors server user roles — keep in sync with `userModel` enum. */
export const USER_ROLE = Object.freeze({
  SUPER_ADMIN: 'superAdmin',
  ADMIN: 'admin',
  SUBSCRIBER: 'subscriber',
  PARTNER: 'partner',
})

export const USER_ROLES = Object.freeze(Object.values(USER_ROLE))

/**
 * Top-level layout the session is in — account, group, or admin. Used by
 * `AccountSwitcher` and scope-neutral pages that keep the current view on
 * navigate (notifications, profile).
 */
export const VIEW_MODE = Object.freeze({
  USER: 'user',
  GROUP: 'group',
  ADMIN: 'admin',
})

/**
 * Resolve the active view mode from session state, not the current route.
 * Scope-neutral routes omit layout meta but preserve scope + `groupMode`.
 *
 * @param {{ groupMode?: boolean }|null|undefined} user
 * @param {string|null|undefined} scopeKind
 * @returns {typeof VIEW_MODE[keyof typeof VIEW_MODE]}
 */
export function resolveViewMode(user, scopeKind) {
  if (scopeKind === SCOPES.ADMIN) return VIEW_MODE.ADMIN
  if (user?.groupMode) return VIEW_MODE.GROUP
  return VIEW_MODE.USER
}

/**
 * Resolve the view mode shown in the header switcher from the current route.
 * Account and group layouts win over session `groupMode`; scope-neutral pages
 * fall back to {@link resolveViewMode}.
 *
 * @param {{ matched?: Array<{ meta?: object }>, meta?: object }} route
 * @param {{ groupMode?: boolean }|null|undefined} user
 * @param {string|null|undefined} scopeKind
 * @returns {typeof VIEW_MODE[keyof typeof VIEW_MODE]}
 */
export function resolveActiveViewMode(route, user, scopeKind) {
  const matched = route.matched ?? []
  if (matched.some((record) => record.meta?.requiresAdmin)) return VIEW_MODE.ADMIN
  if (matched.some((record) => record.meta?.requiresGroupMode)) return VIEW_MODE.GROUP
  if (route.meta?.scopeNeutral) return resolveViewMode(user, scopeKind)
  return VIEW_MODE.USER
}

/**
 * Session shell for router guards — which top-level view the user belongs in.
 *
 * @param {{ groupMode?: boolean }|null|undefined} user
 * @param {string|null|undefined} scopeKind
 * @returns {typeof VIEW_MODE[keyof typeof VIEW_MODE]}
 */
export function resolveSessionShell(user, scopeKind) {
  return resolveViewMode(user, scopeKind)
}

/**
 * Target shell implied by a path, or `null` for auth/maintenance/scope-neutral routes.
 *
 * @param {string} path
 * @param {{ scopeNeutral?: boolean }|undefined} meta
 * @returns {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null}
 */
export function resolvePathShell(path, meta) {
  if (meta?.scopeNeutral) return null
  if (
    path === ROUTE_PATHS.login ||
    path === ROUTE_PATHS.forgotPassword ||
    path === ROUTE_PATHS.maintenance
  ) {
    return null
  }
  if (path === ROUTE_PATHS.group || path.startsWith(`${ROUTE_PATHS.group}/`)) return VIEW_MODE.GROUP
  if (path === ROUTE_PATHS.admin || path.startsWith(`${ROUTE_PATHS.admin}/`)) return VIEW_MODE.ADMIN
  return VIEW_MODE.USER
}

/**
 * Default SPA home for a session shell.
 * USER / GROUP product exits should call `goToV1Overview` instead — these
 * paths only exist as stub safety nets until a real v2 dashboard ships.
 *
 * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]} shell
 * @returns {string}
 */
export function shellHome(shell) {
  if (shell === VIEW_MODE.ADMIN) return `${ROUTE_PATHS.admin}/overview`
  if (shell === VIEW_MODE.GROUP) return `${ROUTE_PATHS.group}/overview`
  return '/overview'
}

/** @param {{ role?: string }|null|undefined} user */
export function isSuperAdmin(user) {
  return user?.role === USER_ROLE.SUPER_ADMIN
}
